import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import packageInfo from '../../../../../../../package.json';
import { environment } from '../../../../../../environments/environment';
import { Observable, Subject, throwError, mergeMap } from 'rxjs';
import { ApiErrorResponse, ApiServiceInterface, FormServiceInterface, ThemeServiceInterface, UserServiceInterface } from '@hutsix/ngxh6';
import { UserInterface } from '../../../../../_generated/api_interfaces/api/user.interface';
import { OrderInterface } from '../../../../../_generated/api_interfaces/api/order.interface';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { LineItemInterface } from '../../../../../_generated/api_interfaces/api/lineItem.interface';
import { CartTableComponent } from '../../tables/cart-table/cart-table.component';
import { CartHelpersService } from '../../../../../services/cart-helpers/cart-helpers.service';

@Component({
    selector: 'app-cart',
    templateUrl: './cart.component.html',
    styleUrls: ['./cart.component.scss'],
    standalone: false,
})
export class CartComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();
    private userId?: string;

    public env: string = environment.env;
    public version: string = packageInfo.version + (environment.env === 'staging' ? '-staging' : '');
    public user$: Observable<ReturnType<UserInterface['apiUsersIdGet']>>;
    public user: ReturnType<UserInterface['apiUsersIdGet']>;
    public orders$: Observable<ReturnType<OrderInterface['apiOrdersGetCollection']>>;
    public order?: ReturnType<OrderInterface['apiOrdersIdGet']>['data'];
    public error: ApiErrorResponse<any>;
    public lineItems?: ReturnType<LineItemInterface['apiLineItemsGetCollection']>['data'];

    public cartTableComponent = CartTableComponent;

    constructor(
        public router: Router,
        @Inject('UserService') public userService: UserServiceInterface,
        @Inject('ApiService') public api: ApiServiceInterface,
        @Inject('ThemeService') public themeService: ThemeServiceInterface,
        @Inject('FormService') public formService: FormServiceInterface,
        public cartHelpers: CartHelpersService,
        public cdRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.userId = this.userService.user?.id;
        this.refreshData();
    }

    refreshData(): void {
        this.orders$ = this.api
            .get({
                url: '/api/v1/orders',
                displayErrors: false,
                query: {
                    userAccount: this.userId,
                    status: 'PENDING',
                },
            })
            .pipe(
                takeUntil(this.unsubscribe$),
                map(res => {
                    if (res.data.length) {
                        this.order = res.data[0];
                    }
                    return res.data;
                }),
                catchError(err => {
                    this.error = err;
                    return throwError(err);
                }),
            );

        this.user$ = this.api.get({ url: '/api/v1/users/' + this.userId, displayErrors: false }).pipe(
            takeUntil(this.unsubscribe$),
            map(res => {
                this.user = res;
                this.cdRef.detectChanges();
                return res;
            }),
            catchError(err => {
                this.error = err;
                this.cdRef.detectChanges();
                return throwError(err);
            }),
        );

        this.cdRef.detectChanges();
    }

    public checkout() {
        this.router.navigate(['/account/checkout']);
    }

    public calculateFeesApplied() {
        return this.cartHelpers.calculateFeesApplied(this.order);
    }

    public calculateTotal() {
        return this.cartHelpers.calculateTotal(this.order);
    }

    public calculateSubtotal() {
        return this.cartHelpers.calculateSubtotal(this.order);
    }

    public calculateGst() {
        return this.cartHelpers.calculateGst(this.order);
    }

    public onLineItemTableResponse($event) {
        this.lineItems = $event;
        this.refreshData();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
